import { memo, useState, useCallback, useMemo } from 'react';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import {
  TextTitle3,
  TextHeadline,
  TextBody,
  TextLabel1,
  TextLabel2,
} from '@cbhq/cds-web/typography';
import { RemoteImage } from '@cbhq/cds-web/media';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { SegmentedTabs } from '@cbhq/cds-web/tabs/SegmentedTabs';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { SummaryCard } from '@/components/common/SummaryCard';
import { DisplayType, format } from '@/utils/numberFormatter';
import { assetBySymbol } from '@/onchain/assets';
import {
  TokenizedEquityAsset,
  EquityHistoryEvent,
  ImpactUnit,
} from './hooks/useTangorPortfolioData';

// USDC should always exist in asset registry; empty string fallback for safety
const USDC_IMAGE_URL = assetBySymbol('USDC')?.imageUrl ?? '';

type SubTab = { id: string; label: string };

type Props = {
  assets: TokenizedEquityAsset[];
  history: EquityHistoryEvent[];
  totalValue: number;
};

const TAB_OPTIONS: SubTab[] = [
  { id: 'assets', label: 'Assets' },
  { id: 'history', label: 'History' },
];

// Asset row component - no top border (no dividers between rows)
const AssetRow = memo(function AssetRow({ asset }: { asset: TokenizedEquityAsset }) {
  return (
    <HStack width="100%" paddingY={2} paddingX={3} alignItems="center">
      {/* Asset */}
      <HStack alignItems="center" gap={1} style={{ flex: 2 }}>
        <RemoteImage source={asset.imageUrl} size="m" shape="circle" alt={asset.symbol} />
        <VStack alignItems="flex-start" gap={0}>
          <TextHeadline as="span">{asset.name}</TextHeadline>
          <TextBody as="span" color="fgMuted">
            {asset.symbol}
          </TextBody>
        </VStack>
      </HStack>

      {/* Token Balance - right aligned */}
      <VStack alignItems="flex-end" style={{ flex: 1 }}>
        <TextBody as="span">
          {format({ value: asset.tokenBalance, type: DisplayType.TokenAmount })}{' '}
          <TextBody as="span" color="fgMuted">
            {asset.symbol}
          </TextBody>
        </TextBody>
      </VStack>

      {/* Shares - right aligned */}
      <VStack alignItems="flex-end" style={{ flex: 1 }}>
        <TextBody as="span">
          {format({ value: asset.shares, type: DisplayType.TokenAmount })}
        </TextBody>
      </VStack>

      {/* USDC Amount - right aligned with icon */}
      <VStack alignItems="flex-end" style={{ flex: 1 }}>
        <HStack alignItems="center" gap={0.5}>
          <RemoteImage source={USDC_IMAGE_URL} size="s" shape="circle" alt="USDC" />
          <TextBody as="span">
            {format({ value: asset.usdcAmount, type: DisplayType.TokenAmount })}{' '}
            <TextBody as="span" color="fgMuted">
              USDC
            </TextBody>
          </TextBody>
        </HStack>
      </VStack>
    </HStack>
  );
});

// History row component - no top border (no dividers between rows)
const HistoryRow = memo(function HistoryRow({ event }: { event: EquityHistoryEvent }) {
  const impactPrefix = event.impact >= 0 ? '+' : '';
  const impactColor = event.impact >= 0 ? 'fgPositive' : 'fgNegative';

  return (
    <HStack width="100%" paddingY={2} paddingX={3} alignItems="center">
      {/* Event Type */}
      <HStack alignItems="center" gap={1} style={{ flex: 2 }}>
        <RemoteImage source={event.tokenImageUrl} size="m" shape="circle" alt={event.tokenSymbol} />
        <VStack alignItems="flex-start" gap={0}>
          <HStack alignItems="center" gap={0.5}>
            <TextHeadline as="span">{event.eventLabel}</TextHeadline>
            {event.ratioLabel && <Tag colorScheme="gray">{event.ratioLabel}</Tag>}
          </HStack>
        </VStack>
      </HStack>

      {/* Token - right aligned */}
      <VStack alignItems="flex-end" gap={0} style={{ flex: 1 }}>
        <TextBody as="span">{event.tokenName}</TextBody>
        <TextBody as="span" color="fgMuted">
          {event.tokenSymbol}
        </TextBody>
      </VStack>

      {/* Impact - right aligned */}
      <VStack alignItems="flex-end" style={{ flex: 1 }}>
        <HStack alignItems="center" gap={0.5}>
          {event.impactUnit === ImpactUnit.USDC && (
            <RemoteImage source={USDC_IMAGE_URL} size="s" shape="circle" alt="USDC" />
          )}
          <TextBody as="span" color={impactColor}>
            {impactPrefix}
            {format({ value: Math.abs(event.impact), type: DisplayType.TokenAmount })}{' '}
            {event.impactUnit}
          </TextBody>
        </HStack>
      </VStack>

      {/* Date - right aligned */}
      <VStack alignItems="flex-end" style={{ flex: 1 }}>
        <TextBody as="span">{event.date}</TextBody>
      </VStack>
    </HStack>
  );
});

// Mobile asset row component using ListCell
const AssetMobileRow = memo(function AssetMobileRow({ asset }: { asset: TokenizedEquityAsset }) {
  return (
    <ListCell
      testID={`asset-mobile-${asset.id}`}
      media={<RemoteImage source={asset.imageUrl} size="l" shape="circle" alt={asset.symbol} />}
      title={<TextHeadline as="span">{asset.name}</TextHeadline>}
      description={
        <TextBody as="span" color="fgMuted">
          {asset.symbol}
        </TextBody>
      }
      detail={
        <VStack alignItems="flex-end" gap={0}>
          <HStack alignItems="center" gap={0.5}>
            <RemoteImage source={USDC_IMAGE_URL} size="s" shape="circle" alt="USDC" />
            <TextBody as="span">
              {format({ value: asset.usdcAmount, type: DisplayType.TokenAmount })}
            </TextBody>
          </HStack>
          <TextLabel2 as="span" color="fgMuted">
            {format({ value: asset.tokenBalance, type: DisplayType.TokenAmount })} {asset.symbol}
          </TextLabel2>
        </VStack>
      }
    />
  );
});

// Mobile history row component using ListCell
const HistoryMobileRow = memo(function HistoryMobileRow({ event }: { event: EquityHistoryEvent }) {
  const impactPrefix = event.impact >= 0 ? '+' : '';
  const impactColor = event.impact >= 0 ? 'fgPositive' : 'fgNegative';

  return (
    <ListCell
      testID={`history-mobile-${event.id}`}
      media={
        <RemoteImage source={event.tokenImageUrl} size="l" shape="circle" alt={event.tokenSymbol} />
      }
      title={
        <HStack alignItems="center" gap={0.5}>
          <TextHeadline as="span">{event.eventLabel}</TextHeadline>
          {event.ratioLabel && <Tag colorScheme="gray">{event.ratioLabel}</Tag>}
        </HStack>
      }
      description={
        <TextBody as="span" color="fgMuted">
          {event.tokenSymbol} · {event.date}
        </TextBody>
      }
      detail={
        <TextBody as="span" color={impactColor}>
          {impactPrefix}
          {format({ value: Math.abs(event.impact), type: DisplayType.TokenAmount })}{' '}
          {event.impactUnit}
        </TextBody>
      }
    />
  );
});

export const TangorEquitiesTabs = memo(function TangorEquitiesTabs({
  assets,
  history,
  totalValue,
}: Props) {
  const { isPhone, isPhoneLandscape } = useBreakpoints();
  const isMobile = useMemo(() => Boolean(isPhone || isPhoneLandscape), [isPhone, isPhoneLandscape]);

  const [activeTab, setActiveTab] = useState<SubTab>(TAB_OPTIONS[0]);

  const handleTabChange = useCallback((tab: { id: string } | null) => {
    if (tab) {
      const newTab = TAB_OPTIONS.find((t) => t.id === tab.id);
      if (newTab) {
        setActiveTab(newTab);
      }
    }
  }, []);

  const header = (
    <VStack gap={2} width="100%">
      {/* SegmentedTabs */}
      <SegmentedTabs tabs={TAB_OPTIONS} activeTab={activeTab} onChange={handleTabChange} />

      {/* Title based on active tab */}
      {activeTab.id === 'assets' ? (
        isMobile ? (
          <VStack alignItems="flex-start" gap={0}>
            <TextTitle3 as="h2">{assets.length} tokenized equities held</TextTitle3>
            <TextHeadline as="span" color="fgMuted">
              {format({ value: totalValue, type: DisplayType.Fiat })}
            </TextHeadline>
          </VStack>
        ) : (
          <HStack alignItems="baseline" gap={1}>
            <TextTitle3 as="h2">{assets.length} tokenized equities held</TextTitle3>
            <TextHeadline as="span" color="fgMuted">
              {format({ value: totalValue, type: DisplayType.Fiat })}
            </TextHeadline>
          </HStack>
        )
      ) : (
        <TextTitle3 as="h2">Past events</TextTitle3>
      )}
    </VStack>
  );

  return (
    <SummaryCard header={header} testID="tangor-equities-tabs">
      {activeTab.id === 'assets' ? (
        isMobile ? (
          // Mobile: ListCell rows without table header
          <>
            {assets.map((asset) => (
              <AssetMobileRow key={asset.id} asset={asset} />
            ))}
          </>
        ) : (
          // Desktop: Table with header
          <>
            {/* Table header with bottom border */}
            <HStack
              width="100%"
              paddingY={1}
              paddingX={3}
              style={{ borderBottom: '1px solid var(--color-line)' }}
            >
              <VStack style={{ flex: 2 }}>
                <TextLabel1 as="span" color="fgMuted">
                  Asset
                </TextLabel1>
              </VStack>
              <VStack alignItems="flex-end" style={{ flex: 1 }}>
                <TextLabel1 as="span" color="fgMuted">
                  Token Balance
                </TextLabel1>
              </VStack>
              <VStack alignItems="flex-end" style={{ flex: 1 }}>
                <TextLabel1 as="span" color="fgMuted">
                  Shares
                </TextLabel1>
              </VStack>
              <VStack alignItems="flex-end" style={{ flex: 1 }}>
                <TextLabel1 as="span" color="fgMuted">
                  USDC Amount
                </TextLabel1>
              </VStack>
            </HStack>

            {/* Asset rows - no dividers between rows */}
            {assets.map((asset) => (
              <AssetRow key={asset.id} asset={asset} />
            ))}
          </>
        )
      ) : isMobile ? (
        // Mobile: ListCell rows without table header
        <>
          {history.map((event) => (
            <HistoryMobileRow key={event.id} event={event} />
          ))}
        </>
      ) : (
        // Desktop: Table with header
        <>
          {/* Table header with bottom border */}
          <HStack
            width="100%"
            paddingY={1}
            paddingX={3}
            style={{ borderBottom: '1px solid var(--color-line)' }}
          >
            <VStack style={{ flex: 2 }}>
              <TextLabel1 as="span" color="fgMuted">
                Event Type
              </TextLabel1>
            </VStack>
            <VStack alignItems="flex-end" style={{ flex: 1 }}>
              <TextLabel1 as="span" color="fgMuted">
                Token
              </TextLabel1>
            </VStack>
            <VStack alignItems="flex-end" style={{ flex: 1 }}>
              <TextLabel1 as="span" color="fgMuted">
                Impact
              </TextLabel1>
            </VStack>
            <VStack alignItems="flex-end" style={{ flex: 1 }}>
              <TextLabel1 as="span" color="fgMuted">
                Date
              </TextLabel1>
            </VStack>
          </HStack>

          {/* History rows - no dividers between rows */}
          {history.map((event) => (
            <HistoryRow key={event.id} event={event} />
          ))}
        </>
      )}
    </SummaryCard>
  );
});
